import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ExportFormat, ShareableAssetType } from '@clipr/lib';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { JobModel } from '../../entities';

export const WINDOW_WIDTH = 520;
export const WINDOW_HEIGHT = 640;

export type ShareMeetingAssetsProps = {
  job: JobModel;
  allowShare?: boolean | null;
};

/** A document asset row (Report/Notes/Transcript) shown with [PDF] [Word] buttons. */
export const DOCUMENT_ASSETS: ShareableAssetType[] = [
  ShareableAssetType.Report,
  ShareableAssetType.Notes,
  ShareableAssetType.Transcript,
];

const ASSET_LABEL: Record<ShareableAssetType, string> = {
  [ShareableAssetType.Report]: 'Report',
  [ShareableAssetType.Notes]: 'Notes',
  [ShareableAssetType.Transcript]: 'Transcript',
  [ShareableAssetType.Audio]: 'Audio Recording (.mp3)',
  [ShareableAssetType.Video]: 'Video Recording (.mp4)',
};

const FORMAT_EXT: Record<ExportFormat, string> = {
  [ExportFormat.Pdf]: 'pdf',
  [ExportFormat.Docx]: 'docx',
};

export class ShareMeetingAssetsWindowState extends StoreNode {
  readonly nodeType = 'ShareMeetingAssetsWindow';

  readonly window = new WindowState(this.store);

  @observable job: JobModel | null = null;
  @observable allowShare: boolean | null = null;
  @observable availableAssets: ShareableAssetType[] = [];
  @observable busyKey: string | null = null;

  constructor(store: Store) {
    super(store);
    makeObservable(this);
    this.window.listen(this.windowListener);
  }

  assetLabel(asset: ShareableAssetType): string {
    return ASSET_LABEL[asset];
  }

  @computed
  get documentRows(): ShareableAssetType[] {
    return DOCUMENT_ASSETS.filter(a => this.availableAssets.includes(a));
  }

  @computed
  get hasAudio(): boolean {
    return this.availableAssets.includes(ShareableAssetType.Audio);
  }

  @computed
  get hasVideo(): boolean {
    return this.availableAssets.includes(ShareableAssetType.Video);
  }

  isBusy(key: string): boolean {
    return this.busyKey === key;
  }

  @action
  open(params: ShareMeetingAssetsProps) {
    this.dispatch('Overlays', 'openWindow', { name: 'ShareMeetingAssetsWindow' });
    this.job = params.job;
    this.allowShare = params.allowShare ?? null;
    this.availableAssets = [];
    void this.loadAssets();
  }

  @action
  private async loadAssets() {
    if (!this.job) return;
    const [res, err] = await this.store.api.shareableAssets({ jobId: this.job.id });
    if (err || !res) {
      notifyError(this, 'Could not load shareable assets.');
      return;
    }
    runInAction(() => {
      this.availableAssets = res.shareableAssets.assets;
    });
  }

  @action
  async exportDocument(asset: ShareableAssetType, format: ExportFormat) {
    if (!this.job || this.busyKey) return;
    const key = `${asset}:${format}`;
    runInAction(() => { this.busyKey = key; });
    try {
      const [res, err] = await this.store.api.exportJobAsset({
        input: { jobId: this.job.id, asset, format },
      });
      if (err || !res) {
        notifyError(this, 'Could not export the document. Please try again.');
        return;
      }
      this.triggerDownload(res.exportJobAsset.downloadUrl, `${this.job.title || 'document'}.${FORMAT_EXT[format]}`);
    } catch {
      notifyError(this, 'Could not export the document. Please try again.');
    } finally {
      runInAction(() => { this.busyKey = null; });
    }
  }

  @action
  async shareMedia(asset: ShareableAssetType) {
    if (!this.job || this.busyKey) return;
    runInAction(() => { this.busyKey = asset; });
    try {
      const [res, err] = await this.store.api.createMediaShortLink({
        input: { jobId: this.job.id, asset },
      });
      if (err || !res) {
        notifyError(this, 'Could not create a share link. Please try again.');
        return;
      }
      await this.copyOrShare(res.createMediaShortLink.url);
    } catch {
      notifyError(this, 'Could not create a share link. Please try again.');
    } finally {
      runInAction(() => { this.busyKey = null; });
    }
  }

  private async copyOrShare(url: string) {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ url, title: this.job?.title ?? 'CLIPr' });
        return;
      } catch {
        // fall through to clipboard if the user dismisses the native sheet
      }
    }
    await navigator.clipboard?.writeText(url);
    notifySuccess(this, 'Link Copied');
  }

  private triggerDownload(url: string, fileName: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  @action
  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close();
        break;
    }
  };

  @action
  close() {
    this.job = null;
    this.availableAssets = [];
    this.busyKey = null;
    this.emit('close');
    this.dispatch('Overlays', 'closeWindow');
  }
}
